import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DashboardProfileContextService } from '@rma/dashboard/authentication/public/user';
import { INDIVIDUAL } from '@rma/generic/domain/types';
import { combineLatest, debounceTime, map, Observable, of, startWith, switchMap } from 'rxjs';
import { AlgoliaSearch } from '../api-algolia/algolia-search.service';
import { DashboardSearchResult, SearchResultGroup } from './search-results.model';
import { sectionIconNameMap } from './section-icons';

@Injectable({
  providedIn: 'root',
})
export class SearchFacade {
  public constructor(
    private readonly algoliaService: AlgoliaSearch,
    private readonly profileContextService: DashboardProfileContextService,
    private readonly translocoService: TranslocoService,
  ) {}

  public initialiseSearchResults(input: Observable<string | null>) {
    const isIndividual$ = this.profileContextService.profileType$.pipe(map((p) => p === INDIVIDUAL));

    return combineLatest([isIndividual$]).pipe(
      switchMap(([isIndividual]) => {
        const suggestions = this.getDefaultSuggestions();
        return input.pipe(
          debounceTime(200),
          switchMap((value) => {
            if (this.startsWithNumber(value)) {
              return of(this.getNumberSuggestions());
            }
            if (value) {
              const filters = isIndividual ? 'ShowAgent:TRUE' : 'ShowAgency:TRUE';
              return this.searchHelp<DashboardSearchResult>(value, { filters });
            }
            return of(suggestions);
          }),
          startWith<DashboardSearchResult[]>(suggestions),
          map((hits) => this.groupBySection(hits)),
        );
      }),
    );
  }

  private searchHelp<T>(value: string, options?: { filters: string }): Observable<Array<T>> {
    return this.algoliaService.search<T>(value, options);
  }

  private startsWithNumber(val: string | null): boolean {
    if (!val) return false;
    const regex = /^[0-9]/;
    return regex.test(val);
  }

  private groupBySection(ungroupedResults: DashboardSearchResult[]): SearchResultGroup[] {
    const groups = ungroupedResults.reduce<{
      [key: string]: DashboardSearchResult[];
    }>((accum, curr) => {
      const key = curr.Section;
      const items = accum[key] ?? [];
      const itemWithIcon = {
        ...curr,
        icon: curr.icon || sectionIconNameMap[key.toLowerCase()],
      };
      return { ...accum, [key]: [...items, itemWithIcon] };
    }, {});

    return Object.entries(groups).map(([section, results]) => ({
      section,
      results,
    }));
  }

  private getDefaultSuggestions(): DashboardSearchResult[] {
    const brand = this.translocoService.translate('dashboard.brand');
    // SAFETY(legacy): unreviewed, pre-dates this rule.
    return [
      {
        Description: `Invite your clients to leave you a review on ${brand}.`,
        Title: 'Request Reviews',
        Section: 'Suggestions',
        icon: 'rma-reviews',
        Url: '/reviews/request/opportunities',
      },
      {
        Description: 'Automatically share your reviews on Google to boost your search rankings.',
        Title: 'Connect Google My Business Profile',
        Section: 'Suggestions',
        icon: 'settings',
        Url: '/google-profile/connect/get-started',
      },
    ] as DashboardSearchResult[];
  }

  private getNumberSuggestions(): DashboardSearchResult[] {
    const brand = this.translocoService.translate('dashboard.brand');
    // SAFETY(legacy): unreviewed, pre-dates this rule.
    return [
      {
        Description: `Invite your clients to leave you a review on ${brand}.`,
        Title: 'Request Reviews',
        Section: 'Suggestions',
        icon: 'rma-reviews',
        Url: '/reviews/request/opportunities',
      },
      {
        Description: 'Manage your active and sold property listings.',
        Title: 'Manage Property Listings',
        Section: 'Suggestions',
        icon: 'cottage',
        Url: '/campaigns/sales',
      },
    ] as DashboardSearchResult[];
  }
}
